import React from 'react';
import Localize from 'react-intl-universal';

import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { formatDateTimeStringToDateTimeObject, isFraunhofer } from '@common/Util';
import { sortByKey } from '@pages/EventDetails/Util';

import ConsentsList from './ConsentsList';

const PersonCard = styled('div')(({ theme }) => ({
  border: '1px solid #ddd',
  borderRadius: '6px',
  padding: '16px',
  marginBottom: '16px',
  background: theme.palette.booking.backgroundField,
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
}));

const PersonHeader = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '12px'
});

const PersonRole = styled('div')({
  fontSize: '14px',
  color: '#777'
});

const PersonName = styled('div')({
  fontWeight: 'bold',
  fontSize: '16px',
  marginTop: '4px'
});

const ViewInfoButton = styled(Button)(({ theme }) => ({
  color: theme.palette.primary.main,
  background: 'none',
  border: 'none',
  padding: 0,
  font: 'inherit',
  textTransform: 'none',
  boxShadow: 'none',
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
    background: 'none'
  }
}));

const PersonDetails = styled('div')({
  marginLeft: 0,
  marginRight: 'auto',
  textAlign: 'left',
  width: 'fit-content',
  '& > div': {
    marginBottom: '4px'
  }
});

const BookingParticipantCard = ({ participant = {}, onViewInfo }) => {
  const sortByOrderNumber = sortByKey('orderNumber');
  const sortedEventConsents = sortByOrderNumber(participant?.attendeeDto?.eventConsents);

  return (
    <>
      <PersonCard>
        <PersonHeader>
          <div>
            <PersonRole>
              {participant.attendeeDto?.isBookingPerson
                ? Localize.get('Booking.bookingPerson')
                : Localize.get('Booking.participant')}
            </PersonRole>
            <PersonName>
              {participant.firstName} {participant.lastName}
            </PersonName>
          </div>
          {!isFraunhofer() && (
            <ViewInfoButton onClick={onViewInfo} variant='text'>
              {Localize.get('EventDetails.viewInformation')}
            </ViewInfoButton>
          )}
        </PersonHeader>

        <PersonDetails>
          {participant?.event?.iliasCourseId && (
            <div>
              <strong>{Localize.get('EventDetails.iliasUserId')}:</strong>{' '}
              {participant?.attendeeDto?.iliasUsername || ''}
            </div>
          )}
          <div>
            <strong>{Localize.get('Booking.company')}:</strong>{' '}
            {participant.company?.name || participant.communicationData?.companyName || ''}
          </div>
          <div>
            <strong>{Localize.get('Booking.email')}:</strong>{' '}
            {participant.emailDtos?.[0]?.email || ''}
          </div>
          <div>
            <strong>{Localize.get('Booking.phone')}:</strong>{' '}
            {participant.phoneDtos?.[0]?.phone || ''}
          </div>
          <div>
            <strong>{Localize.get('Booking.hotelNeeded')}:</strong>{' '}
            {participant.hotelRequest ? Localize.get('App.yes') : Localize.get('App.no')}
            {participant.arrivalDate ? ', ' : ''}
            {formatDateTimeStringToDateTimeObject(participant.arrivalDate)}
            {participant.departureDate ? '-' : ''}
            {formatDateTimeStringToDateTimeObject(participant.departureDate)}
          </div>
          <Typography sx={{ fontWeight: 'bold' }}>
            {Localize.get('EventDetails.participationDocuments')}:
          </Typography>
          {sortedEventConsents.length === 0 ? (
            <Typography variant='span'>
              {Localize.get('EventDetails.noConsentsAvailable')}
            </Typography>
          ) : (
            <ConsentsList sortedEventConsents={sortedEventConsents} />
          )}
        </PersonDetails>
      </PersonCard>
    </>
  );
};

export default BookingParticipantCard;
